import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, MapPin, Mail, Phone, AtSign, CalendarDays } from 'lucide-react';
import { companyInfo, navLinks } from '../../data/mock';
import { Ridgeline } from './Section';

const serviceLinks = ['AI-Enabled Apps', 'SaaS Platforms', 'Data Engineering', 'Business Automation', 'Consulting'];

const registrationLines = [
  'Private Limited (MCA) CIN - U62099HP2023PTC010408',
  'GST Registered GSTIN - 02AAKCG7487J1Z6',
  'DGFT Registered IEC - AAKCG7487J',
  'MSME Registered MSME - UDYAM-HP-04-0023734',
  'DPIIT Startup - CERTIFICATE NO: DIPP155874'
];

const legalLinks = [
  { label: 'Privacy Policy', path: '/privacy-policy' },
  { label: 'Terms of Service', path: '/terms-of-service' },
  { label: 'Cookie Policy', path: '/cookie-policy' }
];

const ColumnTitle = ({ children }) => (
  <h4 className="kicker mb-5" style={{ color: 'rgba(255,255,255,0.45)' }}>{children}</h4>
);

export const Footer = () => {
  return (
    <footer className="gx-section tone-ink scheme-dark" style={{ color: 'rgba(255,255,255,0.85)' }}>
      <div className="gx-layer gx-grid" />
      <div className="gx-glow" style={{ background: 'rgba(34,211,238,0.14)', width: 520, height: 520, top: '-30%', left: '-10%' }} />
      <div className="gx-glow" style={{ background: 'rgba(167,139,250,0.14)', width: 480, height: 480, bottom: '-40%', right: '-10%' }} />

      <div className="gx-content">
        <div className="gx-container py-16" style={{ maxWidth: 1240 }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            {/* Brand Column */}
            <div className="lg:col-span-1">
              <Link to="/" className="flex items-center gap-3 no-underline mb-5">
                <img
                  src="/icons/globistaan-live-logo.png"
                  alt="Globistaan logo"
                  className="h-9 w-auto object-contain"
                />
                <span className="flex flex-col leading-none">
                  <span className="text-xl font-bold" style={{ color: '#ffffff', lineHeight: 1, letterSpacing: '-0.01em', fontFamily: 'var(--font-display)' }}>
                    Globistaan
                  </span>
                  <span
                    className="mt-1 text-[0.6rem] font-medium uppercase"
                    style={{ color: 'rgba(255,255,255,0.66)', letterSpacing: '0.08em', lineHeight: 1.15, fontFamily: 'var(--font-mono)' }}
                  >
                    Technologies and Exports Pvt Ltd
                  </span>
                </span>
              </Link>
              <p className="text-sm leading-relaxed mb-5" style={{ color: 'rgba(255,255,255,0.58)', maxWidth: '300px' }}>
                {companyInfo.subtitle}
              </p>
              <div className="flex flex-col gap-2.5">
                {[
                  { Icon: MapPin, value: companyInfo.location },
                  { Icon: Mail, value: companyInfo.email },
                  { Icon: Phone, value: companyInfo.phone },
                  { Icon: AtSign, value: companyInfo.xHandle },
                  { Icon: CalendarDays, value: companyInfo.incorporationDateShort }
                ].map(({ Icon, value }) => (
                  <div key={value} className="flex items-start gap-2">
                    <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: '#22D3EE' }} />
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.56)', fontFamily: 'var(--font-mono)', lineHeight: 1.5 }}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div>
              <ColumnTitle>Navigation</ColumnTitle>
              <nav className="flex flex-col gap-2.5">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="text-sm no-underline transition-colors duration-200 flex items-center gap-1 group hover:text-white"
                    style={{ color: 'rgba(255,255,255,0.62)' }}
                  >
                    {link.label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#22D3EE' }} />
                  </Link>
                ))}
              </nav>
            </div>

            {/* Services */}
            <div>
              <ColumnTitle>Services</ColumnTitle>
              <nav className="flex flex-col gap-2.5">
                {serviceLinks.map((s) => (
                  <span key={s} className="text-sm" style={{ color: 'rgba(255,255,255,0.62)' }}>
                    {s}
                  </span>
                ))}
              </nav>
            </div>

            {/* Company Registrations */}
            <div>
              <ColumnTitle>Company Registrations</ColumnTitle>
              <nav className="flex flex-col gap-2.5">
                {registrationLines.map((s) => (
                  <span key={s} className="text-sm" style={{ color: 'rgba(255,255,255,0.62)', fontFamily: 'var(--font-mono)', fontSize: '0.74rem', lineHeight: 1.5 }}>
                    {s}
                  </span>
                ))}
              </nav>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'var(--font-mono)' }}>
                &copy; {new Date().getFullYear()} {companyInfo.fullName}. All rights reserved.
              </p>
              <div className="flex items-center gap-6 flex-wrap justify-center">
                {legalLinks.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="text-xs cursor-pointer transition-colors duration-200 no-underline hover:text-white"
                    style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'var(--font-mono)' }}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="relative" style={{ height: 90 }}>
          <Ridgeline opacity={0.5} height={90} />
        </div>
      </div>
    </footer>
  );
};
